/**
 * Platform-service API client for public (pre-auth) and admin endpoints.
 *
 * The resolve endpoint is public (no auth needed — themes the login screen).
 * Admin endpoints use the same auth interceptor as the rest of the portal.
 */

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { LOCAL_STORAGE_KEYS } from '@/constants';
import { tokenRefreshService } from '@/lib/auth/token-refresh';

/** Axios config carrying our one-shot retry marker. */
type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

/* ─── Types ─────────────────────────────────────────────────────────── */

export interface BrandingTokens {
  primary?: string;
  secondary?: string;
  accent?: string;
  [key: string]: string | undefined;
}

export interface TenantBranding {
  displayName: string;
  logoLightUrl?: string | null;
  logoDarkUrl?: string | null;
  faviconUrl?: string | null;
  /**
   * Presentational hint for how the logo pairs with the name:
   * - `wordmark` (default): `logoLightUrl` already contains the brand name →
   *   render it alone.
   * - `mark`: `logoLightUrl` is a square icon → render it beside the
   *   `displayName` as a lockup (see `BrandLockup`).
   */
  logoType?: 'wordmark' | 'mark' | null;
  colorTokens?: BrandingTokens | null;
  fontFamily?: string | null;
  customCss?: string | null;
  supportPhone?: string | null;
  supportEmail?: string | null;
  termsUrl?: string | null;
}

export interface ResolveResponse {
  tenantId: string;
  displayName: string;
  branding: TenantBranding | null;
  enabledProducts: string[];
}

/**
 * Outcome of resolving a host to a tenant — `unmapped` must never stand in
 * for "we couldn't ask", or a platform-service blip locks out every tenant.
 */
export type TenantResolution =
  | { status: 'resolved'; tenant: ResolveResponse }
  | { status: 'unmapped' } // HTTP 404 — no TenantDomain row
  | { status: 'unavailable' }; // timeout, network error, 5xx

/** Narrows to a plain, non-null object — excludes an HTML string, an array, `null`. */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * The minimum shape that distinguishes a real resolve response from a
 * misroute (e.g. a dev-server SPA fallback serving `index.html` on a 200).
 * Deliberately shallow — `branding` is legitimately `null` and
 * `enabledProducts` legitimately varies, so only `tenantId` is checked.
 */
function looksLikeResolveResponse(data: unknown): data is ResolveResponse {
  return isPlainObject(data) && typeof data.tenantId === 'string';
}

/* ─── Public (no-auth) client ───────────────────────────────────────── */

/**
 * Resolve current hostname to a tenant + branding.
 *
 * This is a PUBLIC endpoint — no Bearer token. We use a raw axios
 * call (not the auth-intercepted `api` singleton) because the login
 * page calls this before the user is authenticated.
 *
 * Doesn't trust the transport alone — a misroute (Caddy, the Vite proxy, a
 * CDN, an SPA fallback) can carry a 404 or a 200 with the wrong body, so
 * every ambiguous case is biased toward `unavailable`.
 */
export async function resolveTenant(host: string): Promise<TenantResolution> {
  const base = import.meta.env?.VITE_PLATFORM_API_URL ?? '/platform-api';
  try {
    const resp = await axios.get<unknown>(
      `${base}/api/v1/tenants/resolve`,
      { params: { host }, timeout: 5_000 }
    );
    if (!looksLikeResolveResponse(resp.data)) {
      return { status: 'unavailable' };
    }
    return { status: 'resolved', tenant: resp.data };
  } catch (err) {
    if (
      axios.isAxiosError(err) &&
      err.response?.status === 404 &&
      isPlainObject(err.response.data)
    ) {
      return { status: 'unmapped' };
    }
    return { status: 'unavailable' };
  }
}

/* ─── Authenticated platform API client ─────────────────────────────── */

const PLATFORM_BASE = import.meta.env?.VITE_PLATFORM_API_URL ?? '/platform-api';

/**
 * Dedicated axios instance for platform-service admin endpoints.
 *
 * Do NOT use the offerings-service `api` singleton — its `/api` baseURL
 * routes to the wrong backend.
 *
 * Refresh goes through the `tokenRefreshService` singleton, never a local
 * gate: its `refreshPromise` collapses concurrent rotations across both
 * clients, and parallel refreshes trip server-side replay detection.
 *
 * No `X-Tenant-Id`, unlike the `api` singleton — platform-scoped routes rely
 * on it being absent to skip the cross-tenant check.
 */
export const platformApi = axios.create({
  baseURL: PLATFORM_BASE,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

platformApi.interceptors.request.use(async (config) => {
  // The retry already carries the rotated token; re-resolving would overwrite
  // it with whatever storage holds and replay the stale one.
  if ((config as RetriableConfig)._retry) {
    return config;
  }

  const token =
    (await tokenRefreshService.getValidToken()) ??
    localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

platformApi.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;

    // One attempt only — a 401 that survives a fresh token is a real denial.
    if (error.response?.status !== 401 || !original || original._retry) {
      return Promise.reject(error);
    }
    original._retry = true;

    const result = await tokenRefreshService.refresh();
    if (!result.ok) {
      // A network/cold-start failure preserves the session — bouncing to the
      // login screen would log out an operator whose cookie is still valid.
      if (result.reason !== 'network') {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    original.headers.Authorization = `Bearer ${result.accessToken}`;
    return platformApi(original);
  }
);

